import {
  ApplicationConfig,
  importProvidersFrom,
  ErrorHandler,
} from '@angular/core';
import {
  provideRouter,
  withPreloading,
  PreloadAllModules,
} from '@angular/router';
import {
  provideHttpClient,
  withInterceptors,
  HttpClient,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations'; // NOSONAR
import { provideToastr } from 'ngx-toastr';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

import { routes } from './app.routes';
import { apiHttpInterceptor } from './interceptors/http-interceptor';
import { retryInterceptor } from './interceptors/retry-interceptor';
import { authRefreshInterceptor } from './interceptors/auth-refresh.interceptor';
import { ENVIRONMENT } from './services/logger.service';
import { environment } from '../environments/environment';

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Initialize Sentry only in production
if (environment.production && environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: environment.production ? 'production' : 'development',
  });
}

const animationsProvider = provideAnimations(); // NOSONAR

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENVIRONMENT, useValue: environment },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    provideRouter(routes, withPreloading(PreloadAllModules)),
    animationsProvider,
    provideHttpClient(
      withInterceptors([
        retryInterceptor,
        apiHttpInterceptor,
        authRefreshInterceptor,
      ]),
    ),
    provideToastr(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'fr',
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
  ],
};
